import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download } from 'lucide-react';
import type { EntityRow } from './types';

interface KVTableProps {
  data: EntityRow[];
  onRowClick?: (row: EntityRow) => void;
}

const KVTable: React.FC<KVTableProps> = function ({ data, onRowClick }) {
  const [search, setSearch] = useState('');
  const [topBottomMode, setTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [topBottomN, setTopBottomN] = useState('10');
  const [passMin, setPassMin] = useState('');
  const [passMax, setPassMax] = useState('');
  const [piMin, setPiMin]     = useState('');
  const [piMax, setPiMax]     = useState('');

  const [sortKey, setSortKey] = useState<keyof EntityRow>('passPercentage');
  const [sortAsc, setSortAsc] = useState(false);

  const avgPass = data.length
    ? data.reduce(function (s, r) { return s + r.passPercentage; }, 0) / data.length
    : 0;
  const avgPI = data.length
    ? data.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / data.length
    : 0;

  function handleSort(k: keyof EntityRow) {
    if (sortKey === k) { setSortAsc(!sortAsc); } else { setSortKey(k); setSortAsc(false); }
  }

  // When primary sort is PI or Pass%, the other automatically becomes the secondary sort (same direction)
  const linkedSecKey: keyof EntityRow | null =
    sortKey === 'performanceIndex' ? 'passPercentage' :
    sortKey === 'passPercentage'   ? 'performanceIndex' : null;

  // 1. Search + sort
  const sortedFiltered = [...data]
    .filter(function (r) { return r.name.toLowerCase().includes(search.toLowerCase()); })
    .sort(function (a, b) {
      const av = a[sortKey]; const bv = b[sortKey];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = sortAsc ? av - bv : bv - av;
      else cmp = sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      if (cmp !== 0) return cmp;
      if (linkedSecKey) {
        const as2 = a[linkedSecKey]; const bs2 = b[linkedSecKey];
        if (typeof as2 === 'number' && typeof bs2 === 'number') return sortAsc ? as2 - bs2 : bs2 - as2;
      }
      return 0;
    });

  // 2. Pass% and PI range filters
  const rangeFiltered = sortedFiltered.filter(function (r) {
    const pm = passMin !== '' ? r.passPercentage   >= Number(passMin) : true;
    const px = passMax !== '' ? r.passPercentage   <= Number(passMax) : true;
    const im = piMin   !== '' ? r.performanceIndex >= Number(piMin)   : true;
    const ix = piMax   !== '' ? r.performanceIndex <= Number(piMax)   : true;
    return pm && px && im && ix;
  });

  // 3. Top / Bottom N slice
  const n = Math.max(1, parseInt(topBottomN, 10) || 10);
  const displayRows = topBottomMode === 'top'    ? rangeFiltered.slice(0, n)
                    : topBottomMode === 'bottom' ? rangeFiltered.slice(-n)
                    : rangeFiltered;

  function downloadCSV() {
    const headers = ['Nat. Rank', 'RO Rank', 'KV Name', 'Code', 'State', 'District', 'Sector', 'Appeared', 'Passed', 'Pass%', 'PI'];
    const rows = displayRows.map(function (r) {
      return [
        r.nationalRank ?? '', r.roRank ?? '', r.name, r.kvCode ?? '', r.state ?? '',
        r.district ?? '', r.sector ?? '', r.totalAppeared, r.totalPassed, r.passPercentage, r.performanceIndex,
      ].map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
    });
    const csv  = [headers.map(function (h) { return '"' + h + '"'; }).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'kv-performance.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  function Th(p: { label: string; field: keyof EntityRow }) {
    const isPrimary = sortKey === p.field;
    const isSecondary = linkedSecKey === p.field;
    const SortIcon = isPrimary || isSecondary
      ? (sortAsc ? ArrowUp : ArrowDown)
      : ArrowUpDown;
    const iconClass = isPrimary
      ? 'w-3 h-3 text-[#F36B21]'
      : isSecondary
        ? 'w-3 h-3 text-orange-300'
        : 'w-3 h-3 text-gray-400';
    return (
      <th
        className="py-3 px-3 text-xs font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
        onClick={function () { handleSort(p.field); }}
        title={isSecondary ? 'Co-sorted with ' + (p.field === 'passPercentage' ? 'PI' : 'Pass%') : undefined}
      >
        <span className="flex items-center gap-1">
          {p.label}
          <SortIcon className={iconClass} />
        </span>
      </th>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">KV-wise Performance</h3>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search KV…" value={search}
            onChange={function (e) { setSearch(e.target.value); }}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
        </div>
      </div>

      {/* Filter bar: Top/Bottom N · Pass% range · PI range · CSV */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 py-2.5 px-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500 font-medium">Show:</span>
          <div className="flex rounded overflow-hidden border border-gray-200">
            {(['none', 'top', 'bottom'] as const).map(function (m) {
              return (
                <button key={m} onClick={function () { setTopBottomMode(m); }}
                  className={'px-2 py-1 transition-colors ' + (topBottomMode === m ? 'bg-[#F36B21] text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}>
                  {m === 'none' ? 'All' : m === 'top' ? 'Top' : 'Bottom'}
                </button>
              );
            })}
          </div>
          <input type="number" min={1} value={topBottomN} onChange={function (e) { setTopBottomN(e.target.value); }}
            disabled={topBottomMode === 'none'}
            className="w-12 px-1.5 py-1 rounded border border-gray-200 text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">Pass%:</span>
          <input type="number" placeholder="min" value={passMin} onChange={function (e) { setPassMin(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={passMax} onChange={function (e) { setPassMax(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">PI:</span>
          <input type="number" placeholder="min" value={piMin} onChange={function (e) { setPiMin(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={piMax} onChange={function (e) { setPiMax(e.target.value); }}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-orange-300" />
        </div>
        <div className="flex-1" />
        <span className="text-gray-400">{displayRows.length} row{displayRows.length !== 1 ? 's' : ''}</span>
        <button onClick={downloadCSV}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-[#F36B21] text-white font-medium hover:bg-orange-600 transition-colors">
          <Download className="w-3 h-3" />CSV
        </button>
      </div>
      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <Th label="Nat. Rank" field="nationalRank" />
              <Th label="RO Rank" field="roRank" />
              <Th label="KV Name" field="name" />
              <Th label="Code" field="kvCode" />
              <Th label="State" field="state" />
              <Th label="District" field="district" />
              <Th label="Sector" field="sector" />
              <Th label="Appeared" field="totalAppeared" />
              <Th label="Passed" field="totalPassed" />
              <Th label={'Pass % (avg ' + avgPass.toFixed(1) + '%)'} field="passPercentage" />
              <Th label={'PI (avg ' + avgPI.toFixed(1) + ')'} field="performanceIndex" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayRows.map(function (row) {
              return (
                <tr
                  key={row.name}
                  className={'hover:bg-orange-50 transition-colors' + (onRowClick ? ' cursor-pointer' : '')}
                  onClick={function () { if (onRowClick) onRowClick(row); }}
                >
                  <td className="py-2.5 px-3 text-center font-semibold text-orange-600">{row.nationalRank ?? '—'}</td>
                  <td className="py-2.5 px-3 text-center font-semibold text-blue-600">{row.roRank ?? '—'}</td>
                  <td className="py-2.5 px-3 font-medium text-gray-800 max-w-[200px] truncate" title={row.name}>
                    {row.name}
                  </td>
                  <td className="py-2.5 px-3 text-gray-500 font-mono text-xs">{row.kvCode ?? '–'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{row.state ?? '–'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{row.district ?? '–'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{row.sector ?? '–'}</td>
                  <td className="py-2.5 px-3 text-gray-700">{row.totalAppeared.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-green-700 font-medium">{row.totalPassed.toLocaleString()}</td>
                  <td className="py-2.5 px-3">
                    <span className={
                      'px-2 py-0.5 rounded-full text-xs font-semibold ' +
                      (row.passPercentage >= avgPass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                    }>
                      {row.passPercentage}%
                    </span>
                  </td>
                  <td className={
                    'py-2.5 px-3 font-medium ' +
                    (row.performanceIndex >= avgPI ? 'text-green-700' : 'text-red-600')
                  }>
                    {row.performanceIndex}
                  </td>
                </tr>
              );
            })}
            {displayRows.length === 0 && (
              <tr>
                <td colSpan={11} className="py-8 text-center text-gray-400">No KVs found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KVTable;
